import { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { context } from "utils/Provider";
import { enqueueSnackbar } from "notistack";
import enqueueError from "utils/enqueueError";
import { useQueryProcessedViewsDataQuery, useUnmatchProcessedMutation } from "graphql/types/graphql";

interface PhaseTableRow {
  FX_UNIQUE_ID?: string;
  [key: string]: unknown;
}

interface Column {
  header: string;
  accessor: string;
}

export const useManualUnmatch = (tableName: string, identifier?: string) => {
  const { setIsLoading, selectedCube } = useContext(context);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [tableData, setTableData] = useState<PhaseTableRow[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const queryVariables = useMemo(
    () => ({
      cubeId: selectedCube.cubeId,
      filter: {
        viewName: tableName,
      },
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      ...(identifier ? { identifier, includeMatched: true } : {}),
    }),
    [selectedCube.cubeId, tableName, rowsPerPage, page, identifier]
  );

  const { data, loading, error } = useQueryProcessedViewsDataQuery({
    notifyOnNetworkStatusChange: true,
    variables: queryVariables,
    skip: !tableName || !selectedCube.cubeId,
    fetchPolicy: "cache-and-network",
    onCompleted: (result) => {
      setIsLoading(false);
      if (result?.queryProcessedViewsData?.edges) {
        let edgesToUse = result.queryProcessedViewsData.edges;

        if (identifier) {
          const scoreColumn = `SCORE_${identifier}`;
          edgesToUse = edgesToUse.filter((row) => {
            const scoreValue = row[scoreColumn];
            return scoreValue !== undefined && scoreValue !== null && !!scoreValue;
          });
        }

        setTableData(edgesToUse as PhaseTableRow[]);
        setTotalCount(result.queryProcessedViewsData.totalCount || 0);
      } else {
        setTableData([]);
        setTotalCount(0);
      }
    },
    onError: (err) => {
      setIsLoading(false);
      enqueueError(err);
    },
  });

  const [unmatch, { loading: unmatchLoading }] = useUnmatchProcessedMutation({
    update: (cache, { data }) => {
      if (data?.unmatchProcessed?.success && identifier) {
        cache.evict({ fieldName: "queryProcessedViewsData" });

        const updatedTableData = tableData.filter((row) => !selectedRows.includes(String(row.FX_UNIQUE_ID)));
        setTableData(updatedTableData);
        setTotalCount(totalCount - selectedRows.length);
        setSelectedRows([]);
        enqueueSnackbar(`Successfully unmatched ${selectedRows.length} records`, { variant: "success" });
      } else if (!identifier) {
        enqueueSnackbar("Unmatch operation not supported without identifier", { variant: "warning" });
      } else {
        enqueueError(new Error(data?.unmatchProcessed?.error || "Failed to unmatch records"));
      }
    },
    onError: (err) => {
      enqueueError(err);
    },
  });

  useEffect(() => {
    setIsLoading(loading || unmatchLoading);
  }, [loading, unmatchLoading, setIsLoading]);

  const allColumns: Column[] = useMemo(() => {
    return tableData.length > 0
      ? Object.keys(tableData[0]).map((key) => ({
          header: key.toUpperCase(),
          accessor: key,
        }))
      : [];
  }, [tableData]);

  const handleSelectRow = useCallback((fxUniqueId: string) => {
    setSelectedRows((prev) =>
      prev.includes(fxUniqueId) ? prev.filter((id) => id !== fxUniqueId) : [...prev, fxUniqueId]
    );
  }, []);

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        const validRows = tableData
          .filter((row) => row.FX_UNIQUE_ID !== undefined && row.FX_UNIQUE_ID !== null)
          .map((row) => String(row.FX_UNIQUE_ID));
        setSelectedRows(validRows);
      } else {
        setSelectedRows([]);
      }
    },
    [tableData]
  );

  const handleUnmatch = useCallback(async () => {
    if (selectedRows.length === 0) {
      enqueueSnackbar("No rows selected for unmatching", { variant: "warning" });
      return;
    }

    if (!identifier) {
      enqueueSnackbar("Cannot unmatch without an identifier", { variant: "error" });
      return;
    }

    try {
      setIsLoading(true);
      await unmatch({
        variables: {
          tableName,
          identifier: identifier.toString(),
          ids: selectedRows,
        },
      });
    } catch (e) {
      enqueueSnackbar(`Error unmatching records: ${e instanceof Error ? e.message : "Something went wrong"}`, {
        variant: "error",
        autoHideDuration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [selectedRows, identifier, setIsLoading, unmatch, tableName]);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  return {
    selectedRows,
    tableData,
    allColumns,
    loading,
    error,
    unmatchLoading,
    totalCount,
    page,
    rowsPerPage,
    handleSelectRow,
    handleSelectAll,
    handleUnmatch,
    handleChangePage,
    handleChangeRowsPerPage,
    setTableData,
    setTotalCount,
    setSelectedRows,
  };
};
